import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Inbox, AlertTriangle, Search, X } from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTickets, useTicketCompanies } from '@/queries/tickets'
import { EmptyState } from '@/components/ui/empty-state'
import { ListSkeleton } from '@/components/ui/skeleton'
import { TicketRow } from './TicketRow'
import { statusLabels } from '@/lib/status'
import type { Ticket, TicketStatus } from '@/types/ticket'

const STATUSES: Array<TicketStatus | 'all'> = ['all', 'open', 'in_progress', 'pending', 'resolved', 'closed']
const PRIORITIES = ['high', 'normal', 'low'] as const

const SORT_OPTIONS = [
  { value: '-lastMessageAt', label: 'Latest activity' },
  { value: '-createdAt', label: 'Newest' },
  { value: 'createdAt', label: 'Oldest' },
  { value: 'priority', label: 'Priority' },
]

/** Queue filters — mirrored into the /admin URL search params by the route. */
export interface QueueFilters {
  status?: string
  priority?: string
  assignee?: string
  companyId?: string
  q?: string
  sort?: string
}

interface AdminUser {
  _id: string
  name?: string
  email?: string
}

function RailButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Button
      variant={active ? 'secondary' : 'ghost'}
      size="sm"
      className="justify-start w-full font-normal"
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

function RailLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2.5 mt-4 mb-1 text-etn-micro font-semibold tracking-[0.05em] uppercase text-etn-text-label first:mt-0">
      {children}
    </div>
  )
}

export function AdminQueueView({
  filters,
  onChange,
}: {
  filters: QueueFilters
  onChange: (next: QueueFilters) => void
}) {
  const set = (patch: Partial<QueueFilters>) => onChange({ ...filters, ...patch })

  // Debounced search: local input state, push to the URL after a pause.
  const [searchText, setSearchText] = useState(filters.q ?? '')
  useEffect(() => { setSearchText(filters.q ?? '') }, [filters.q])
  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchText.trim()
      if ((filters.q ?? '') !== trimmed) set({ q: trimmed || undefined })
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  const params: Record<string, unknown> = {}
  if (filters.status && filters.status !== 'all') params.status = filters.status
  if (filters.priority) params.priority = filters.priority
  if (filters.assignee) params.assignedTo = filters.assignee
  if (filters.companyId) params.companyId = filters.companyId
  if (filters.q) params.q = filters.q
  if (filters.sort) params.sort = filters.sort

  const { data, isLoading, error, refetch } = useTickets(params)
  const companies = useTicketCompanies()
  const admins = useQuery({
    queryKey: ['support-tickets', 'admins'],
    queryFn: () => api<{ data: AdminUser[] }>('/support-tickets/admins'),
    staleTime: 5 * 60_000,
  })

  const hasFilters = Boolean(
    (filters.status && filters.status !== 'all') || filters.priority || filters.assignee || filters.companyId || filters.q,
  )

  return (
    <div className="flex gap-8">
      <nav className="flex w-48 shrink-0 flex-col gap-0.5">
        <RailLabel>Status</RailLabel>
        {STATUSES.map(s => (
          <RailButton
            key={s}
            active={(filters.status ?? 'all') === s}
            onClick={() => set({ status: s === 'all' ? undefined : s })}
          >
            {s === 'all' ? 'All' : statusLabels[s as TicketStatus]}
          </RailButton>
        ))}

        <RailLabel>Assignee</RailLabel>
        <RailButton active={!filters.assignee} onClick={() => set({ assignee: undefined })}>Anyone</RailButton>
        <RailButton active={filters.assignee === 'me'} onClick={() => set({ assignee: 'me' })}>Assigned to me</RailButton>
        <RailButton active={filters.assignee === 'none'} onClick={() => set({ assignee: 'none' })}>Unassigned</RailButton>
        {admins.data?.data.map(a => (
          <RailButton
            key={a._id}
            active={filters.assignee === a._id}
            onClick={() => set({ assignee: a._id })}
          >
            <span className="truncate">{a.name ?? a.email ?? a._id}</span>
          </RailButton>
        ))}

        <RailLabel>Priority</RailLabel>
        <div className="flex gap-1 px-1">
          {PRIORITIES.map(p => {
            const active = filters.priority === p
            return (
              <button
                key={p}
                type="button"
                onClick={() => set({ priority: active ? undefined : p })}
                className={`flex-1 rounded-etn-pill border px-1.5 py-1 text-etn-micro font-semibold capitalize transition-colors ${
                  active ? 'border-transparent bg-primary text-primary-foreground' : 'border-etn-border-hairline hover:bg-etn-control-subtle'
                }`}
              >
                {p}
              </button>
            )
          })}
        </div>
      </nav>

      <div className="flex-1 min-w-0">
        <div className="mb-4 flex items-center gap-3">
          <h1 className="text-lg font-semibold text-etn-text">Queue</h1>
          <span className="text-etn-micro text-etn-text-soft tabular-nums">
            {data ? `${data.total} ticket${data.total === 1 ? '' : 's'}` : ''}
          </span>
          {hasFilters && (
            <button
              type="button"
              onClick={() => onChange({ sort: filters.sort })}
              className="inline-flex items-center gap-1 text-etn-micro text-etn-text-soft hover:text-etn-text"
            >
              <X className="h-3 w-3" /> Clear filters
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-etn-text-soft" />
              <Input
                pill
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search subjects…"
                className="w-56 pl-9"
              />
            </div>
            <select
              aria-label="Company"
              className="h-[var(--etn-control-height-input)] rounded-etn-menu border border-etn-border-input bg-etn-surface px-3 text-etn-control text-etn-text-cell max-w-44"
              value={filters.companyId ?? ''}
              onChange={e => set({ companyId: e.target.value || undefined })}
            >
              <option value="">All companies</option>
              {companies.data?.data.map(c => (
                <option key={c._id} value={c._id}>{c.name ?? c._id}</option>
              ))}
            </select>
            <select
              aria-label="Sort"
              className="h-[var(--etn-control-height-input)] rounded-etn-menu border border-etn-border-input bg-etn-surface px-3 text-etn-control text-etn-text-cell"
              value={filters.sort ?? '-lastMessageAt'}
              onChange={e => set({ sort: e.target.value === '-lastMessageAt' ? undefined : e.target.value })}
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {isLoading && <ListSkeleton rows={5} />}

        {error && (
          <div className="flex flex-col items-center gap-3 py-16">
            <EmptyState
              icon={<AlertTriangle />}
              title="Couldn't load the queue."
              body="Something went wrong. Please try again."
            />
            <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
          </div>
        )}

        {!isLoading && !error && (data?.data.length ?? 0) === 0 && (
          hasFilters
            ? <EmptyState icon={<Inbox />} title="No tickets match." body="Try widening the filters." />
            : <EmptyState icon={<Inbox />} title="Queue is clear." body="No tickets need triage right now." />
        )}

        {!isLoading && !error && (data?.data.length ?? 0) > 0 && (
          <div className="divide-y divide-etn-border-row rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper">
            {data!.data.map((t: Ticket) => (
              <TicketRow
                key={t._id}
                ticket={t}
                relation={t.viewerRelation ?? 'admin'}
                hrefBase="/admin/tickets"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
